import * as XLSX from "xlsx";

const parseAmount = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "object" && value !== null) {
    const currencies = Object.keys(value);
    if (currencies.length > 0) {
      return parseFloat(value[currencies[0]]) || 0;
    }
  }
  const numStr = String(value).replace(/[^0-9.-]/g, "");
  return parseFloat(numStr) || 0;
};

const formatDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

const setColumnWidths = (worksheet, widths) => {
  worksheet["!cols"] = widths.map((width) => ({ wch: width }));
};

const getSaleType = (sale) => {
  const items = Array.isArray(sale?.sale_items) ? sale.sale_items : [];
  if (!items.length) return "retail";

  const types = new Set(
    items.map((item) => {
      const salePrice = parseAmount(item?.sale_price);
      const wholesalePrice = parseAmount(item?.product_details?.wholesale_price);
      const retailPrice = parseAmount(
        item?.product_details?.retail_price ?? item?.product_details?.sale_price
      );

      if (wholesalePrice > 0 && salePrice === wholesalePrice) return "wholesale";
      if (retailPrice > 0 && salePrice === retailPrice) return "retail";
      if (wholesalePrice > 0 && retailPrice <= 0) return "wholesale";
      return "retail";
    })
  );

  return types.size === 1 ? Array.from(types)[0] : "mixed";
};

export const exportComprehensiveSaleReportToExcel = (data) => {
  const {
    sales = [],
    payments = [],
    returns = [],
    balancePayments = [],
    summary = {},
    selectedCustomerName = "",
    selectedSaleType = "all",
    startDate = "",
    endDate = "",
  } = data || {};

  const workbook = XLSX.utils.book_new();

  const salesRows = sales.map((sale, index) => {
    const saleReturns = returns.filter((item) => item.sale === sale.id);
    const returnAmount = saleReturns.reduce(
      (sum, item) => sum + parseAmount(item.total_refound_amount),
      0
    );
    const subtotal = parseAmount(sale.total_amount);
    const discount = parseAmount(sale.discount);
    const paid = parseAmount(sale.total_paid);
    const afterDiscount = parseAmount(
      sale.effective_total_amount ?? sale.total_amount
    );
    const outstanding = Math.max(0, afterDiscount - paid - returnAmount);

    return {
      "#": index + 1,
      "Sale ID": sale.id,
      Customer: sale.customer_details?.name || "Walk-in Customer",
      Date: sale.sale_date || "",
      Type: getSaleType(sale),
      "Invoice Subtotal": subtotal,
      "Discount Amount": discount,
      "Net Invoice": afterDiscount,
      "Paid Amount": paid,
      Outstanding: outstanding,
      Status:
        outstanding <= 0 ? "Paid" : paid > 0 ? "Partial" : "Unpaid",
    };
  });

  const paymentRows = payments.map((payment, index) => ({
    "#": index + 1,
    "Payment ID": payment.id,
    "Sale ID": payment.sale,
    Amount: parseAmount(payment.amount),
    Date: formatDate(payment.payment_date),
    Method: payment.payment_method || "Cash",
    Currency: payment.currency_details?.title || "",
  }));

  const returnRows = returns.map((returnItem, index) => ({
    "#": index + 1,
    "Return ID": returnItem.id,
    "Sale ID": returnItem.sale,
    "Return Date": formatDate(returnItem.return_date),
    Reason: returnItem.reason || "",
    "Refund Amount": parseAmount(returnItem.total_refound_amount),
    Items: returnItem.return_items?.length || 0,
  }));

  const balancePaymentRows = balancePayments.map((payment, index) => ({
    "#": index + 1,
    "Payment ID": payment.id,
    Customer: payment.customer_details?.name || "",
    Amount: parseAmount(payment.amount),
    Date: formatDate(payment.payment_date),
    Currency: payment.currency_details?.title || "",
    Notes: payment.notes || "",
  }));

  const summaryRows = [
    { Metric: "Customer", Value: selectedCustomerName || "All Customers" },
    { Metric: "Sale Type Filter", Value: selectedSaleType || "all" },
    { Metric: "Start Date", Value: startDate || "All" },
    { Metric: "End Date", Value: endDate || "All" },
    { Metric: "Sales Count", Value: summary.total_sales?.count || 0 },
    { Metric: "Invoice Subtotal", Value: summary.total_sales?.amount || 0 },
    {
      Metric: "Discount Amount",
      Value: summary.total_sales?.discount_amount || 0,
    },
    {
      Metric: "Net Invoice Amount",
      Value: summary.total_sales?.after_discount_amount || 0,
    },
    { Metric: "Paid Amount", Value: summary.total_paid || 0 },
    {
      Metric: "Outstanding Amount",
      Value:
        summary.outstanding_including_previous ??
        summary.outstanding_amount ??
        0,
    },
    { Metric: "Sale Payments", Value: summary.total_payments?.amount || 0 },
    {
      Metric: "Balance Payments",
      Value: summary.total_balance_payments?.amount || 0,
    },
    { Metric: "Revenue Collected", Value: summary.total_revenue || 0 },
    {
      Metric: "Wholesale Sales",
      Value: summary.sale_type_breakdown?.wholesale?.amount || 0,
    },
    {
      Metric: "Wholesale Count",
      Value: summary.sale_type_breakdown?.wholesale?.count || 0,
    },
    {
      Metric: "Retail Sales",
      Value: summary.sale_type_breakdown?.retail?.amount || 0,
    },
    {
      Metric: "Retail Count",
      Value: summary.sale_type_breakdown?.retail?.count || 0,
    },
    { Metric: "Returns", Value: summary.total_returns?.amount || 0 },
    { Metric: "Net Sales", Value: summary.net_sales || 0 },
  ];

  const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
  setColumnWidths(summarySheet, [28, 20]);
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

  const salesSheet = XLSX.utils.json_to_sheet(salesRows);
  setColumnWidths(salesSheet, [5, 10, 24, 14, 12, 16, 16, 14, 14, 14, 12]);
  XLSX.utils.book_append_sheet(workbook, salesSheet, "Sales");

  const paymentsSheet = XLSX.utils.json_to_sheet(paymentRows);
  setColumnWidths(paymentsSheet, [5, 12, 10, 14, 14, 18, 12]);
  XLSX.utils.book_append_sheet(workbook, paymentsSheet, "Sale Payments");

  const returnsSheet = XLSX.utils.json_to_sheet(returnRows);
  setColumnWidths(returnsSheet, [5, 12, 10, 14, 24, 16, 10]);
  XLSX.utils.book_append_sheet(workbook, returnsSheet, "Returns");

  const balancePaymentsSheet = XLSX.utils.json_to_sheet(balancePaymentRows);
  setColumnWidths(balancePaymentsSheet, [5, 12, 24, 14, 14, 12, 26]);
  XLSX.utils.book_append_sheet(
    workbook,
    balancePaymentsSheet,
    "Balance Payments"
  );

  const timestamp = new Date()
    .toISOString()
    .replace(/[:.]/g, "-")
    .substring(0, 19);
  XLSX.writeFile(workbook, `Comprehensive_Sales_Report_${timestamp}.xlsx`);
};
